import { useCallback, useEffect, useState } from "react";

import { DocumentCategory } from "@/constants/documents";
import { useAllCaseFiles } from "@/hooks/queries/use-files";

import { FileWithMetadata } from "./types";

export const useClassificationEditor = (
  files: FileWithMetadata[],
  caseNumber?: string
) => {
  const [displayFiles, setDisplayFiles] = useState<FileWithMetadata[]>(
    files.map((file) => ({ ...file }))
  );
  const [modifiedFiles, setModifiedFiles] = useState<Map<string, FileWithMetadata>>(
    new Map()
  );

  const { data: allCaseFiles = [] } = useAllCaseFiles({
    case_number: caseNumber || "",
    enabled: !!caseNumber,
  });

  // Sync when files prop changes
  useEffect(() => {
    setDisplayFiles(files.map((file) => ({ ...file })));
    setModifiedFiles(new Map());
  }, [files]);

  const getFileClassification = useCallback(
    (file: FileWithMetadata): string => {
      const fileId = file.id || file.fileId;
      if (fileId && modifiedFiles.has(fileId)) {
        const modifiedFile = modifiedFiles.get(fileId);
        return modifiedFile ? modifiedFile.classification : file.classification;
      }
      return file.classification;
    },
    [modifiedFiles]
  );

  const hasOtherLOI = useCallback(
    (currentFileId?: string) => {
      for (const f of displayFiles) {
        const fId = f.id || f.fileId;
        if (fId && currentFileId && fId === currentFileId) continue;
        const effective = getFileClassification(f);
        if (effective === DocumentCategory.LOI) return true;
      }

      for (const [id, mf] of modifiedFiles.entries()) {
        if (currentFileId && id === currentFileId) continue;
        if (mf.classification === DocumentCategory.LOI) return true;
      }

      const allFilesList = allCaseFiles as unknown as FileWithMetadata[];
      for (const f of allFilesList || []) {
        const fId = f.id || f.fileId;
        if (fId && currentFileId && fId === currentFileId) continue;
        if (f.classification === DocumentCategory.LOI) return true;
      }

      return false;
    },
    [displayFiles, getFileClassification, modifiedFiles, allCaseFiles]
  );

  const setClassification = useCallback(
    (file: FileWithMetadata, classification: string) => {
      const fileId = file.id || file.fileId;
      if (!fileId) return;

      setModifiedFiles((prev) => {
        const updated = new Map(prev);
        if (classification === file.classification) {
          updated.delete(fileId);
        } else {
          updated.set(fileId, { ...file, classification });
        }
        return updated;
      });
    },
    []
  );

  const findDisplayFile = useCallback(
    (file: FileWithMetadata): FileWithMetadata => {
      const fileIndex = displayFiles.findIndex(
        (f) =>
          (f.id && f.id === file.id) ||
          (f.fileId && f.fileId === file.fileId) ||
          f.filename === file.filename
      );
      return fileIndex >= 0 ? displayFiles[fileIndex] : file;
    },
    [displayFiles]
  );

  const getPageRange = useCallback((file: FileWithMetadata): string => {
    if (file.startPage != null && file.endPage != null) {
      return file.startPage === file.endPage
        ? `Page ${file.startPage}`
        : `Page ${file.startPage}-${file.endPage}`;
    }
    return "";
  }, []);

  return {
    displayFiles,
    modifiedFiles,
    getFileClassification,
    hasOtherLOI,
    setClassification,
    findDisplayFile,
    getPageRange,
  };
};
